import { useState, useEffect, useCallback } from "react";
import { axiosInstance } from "config/api";

const normalizeClinic = (clinic) => {
  if (!clinic || typeof clinic !== "object") {
    return {
      id: "",
      clinicName: "",
      clinicEmail: "",
      phone: "",
      whatsapp: "",
      address: "",
      upiDetails: "",
      bankDetails: "",
      clinicIconURL: "",
      createdAt: "",
    };
  }

  const clinicId =
    clinic.id || clinic._id || clinic.clinic_id || clinic.clinicId || "";
  const name = clinic.clinic_name || clinic.name || clinic.clinicName || "";
  const email = clinic.clinic_email || clinic.email || "";
  const phone = clinic.phoneno || clinic.phone || clinic.phone_no || "";
  const whatsapp = clinic.whatsapp_no || clinic.whatsapp || "";
  const address = clinic.address || clinic.location || "";
  const upiDetails = clinic.upi_details || clinic.upi || "";
  const rawBankDetails = clinic.bank_details || clinic.bank || "";
  const icon = clinic.clinic_icon || clinic.logo || "";
  const createdAt = clinic.created_at || clinic.createdAt || clinic.regdate || "";

  const bankDetails =
    rawBankDetails && typeof rawBankDetails === "object"
      ? [
          rawBankDetails.account_holder,
          rawBankDetails.account_number,
          rawBankDetails.bank_name,
          rawBankDetails.ifsc_code,
        ]
          .filter(Boolean)
          .join(" | ")
      : rawBankDetails || "";

  return {
    id: clinicId ? String(clinicId) : "",
    clinicName: name,
    clinicEmail: email,
    phone,
    whatsapp,
    address,
    upiDetails,
  bankDetails,
    clinicIconURL: icon,
    createdAt,
    // Additional fields that may be present on clinic/allClinicsWithFullData responses
    ownerName: clinic.owner_name || clinic.owner || clinic.clinic_owner || clinic.ownerName || "",
    gstin: clinic.gstin || clinic.GSTIN || clinic.tax_id || "",
    pan: clinic.pan || clinic.PAN || "",
    timezone: clinic.timezone || clinic.tz || "",
    currency: clinic.currency || "",
    city: clinic.city || clinic.town || "",
    state: clinic.state || "",
    country: clinic.country || "",
    pincode: clinic.pincode || clinic.zip || clinic.postcode || "",
    isActive: !!(clinic.is_active || clinic.active || clinic.enabled || true),
    isBlocked: !!(clinic.is_blocked || clinic.blocked || clinic.disabled),
    metadata: clinic.metadata || clinic.meta || {},
    gmeetStatus: (clinic.gmeet_status || clinic.gmeet_connected || clinic.gmeet || clinic.calendar_connected) ? 'connected' : 'not_connected',
  };
};

export const useClinicList = () => {
  const [clinics, setClinics] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchClinics = useCallback(async () => {
    setIsLoading(true);
    try {
      // Prefer clinic/allClinicsWithFullData endpoint; fall back to existing endpoint if unavailable
      let response;
      try {
        response = await axiosInstance.get("/api/clinic/allClinicsWithFullData", { validateStatus: (status) => status < 500 });
        // If return is empty or not successful, try fallback
        if (!response || response.status === 404 || !response.data) {
          response = await axiosInstance.get("/api/clinic/allClinics", { validateStatus: (status) => status < 500 });
        }
      } catch (e) {
        // if clinic/allClinicsWithFullData endpoint 404s or errors, fallback to legacy endpoint
        response = await axiosInstance.get("/api/clinic/allClinics", { validateStatus: (status) => status < 500 });
      }

      let clinicArray = [];
      const { data } = response;

      if (Array.isArray(data)) {
        clinicArray = data;
      } else if (data && Array.isArray(data.clinics)) {
        clinicArray = data.clinics;
      } else if (data && Array.isArray(data.data)) {
        clinicArray = data.data;
      } else if (data && typeof data === "object") {
        clinicArray = Object.values(data).filter((value) =>
          value && typeof value === "object"
        );
      }

      const normalizedClinics = clinicArray.map(normalizeClinic);

      // Try to fetch clinic lock statuses from the backend and merge into clinics.
      // We try a few candidate endpoints to be tolerant of different backends/environments.
      const lockEndpoints = [
        "/api/clinic-locks",
      ];

      let lockData = null;
      for (const ep of lockEndpoints) {
        try {
          const res = await axiosInstance.get(ep, { validateStatus: (s) => s < 500 });
          // Accept responses that contain an array of clinics or an array at the root
          const d = res?.data;
          if (d) {
            if (Array.isArray(d.clinics) || Array.isArray(d)) {
              lockData = d;
              break;
            }
            // Some APIs return { status, total, clinics: [...] }
            if (d.clinics && Array.isArray(d.clinics)) {
              lockData = d;
              break;
            }
          }
        } catch (e) {
          // try next endpoint silently
          // eslint-disable-next-line no-console
          console.debug(`lock status endpoint failed: ${ep}`, e?.message || e);
          continue;
        }
      }

      if (lockData) {
        const locksArray = Array.isArray(lockData.clinics) ? lockData.clinics : Array.isArray(lockData) ? lockData : (lockData.data && Array.isArray(lockData.data) ? lockData.data : []);
        const lockMap = new Map();
        locksArray.forEach((item) => {
          const cid = item.clinic_id || item.clinicId || item.id || item.clinic_id;
          if (cid !== undefined && cid !== null) {
            lockMap.set(String(cid), !!(item.is_locked || item.isLocked || item.locked));
          }
        });

        const merged = normalizedClinics.map((c) => ({
          ...c,
          // prefer explicit lock from API, fallback to any existing property or false
          isLocked: lockMap.has(c.id) ? lockMap.get(c.id) : !!(c.isLocked || c.locked || false),
        }));

        setClinics(merged);
      } else {
        // No lock info available; set normalized clinics as-is
        setClinics(normalizedClinics);
      }
      setError(null);
    } catch (err) {
      console.error("Error fetching clinics:", err);
      setClinics([]);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClinics();
  }, [fetchClinics]);

  return {
    clinics,
    setClinics,
    isLoading,
    error,
    refreshClinics: fetchClinics,
  };
};
